import _ from "lodash";
import { DateTime } from "luxon";
import { ReactElement, useMemo } from "react";

import { palette } from "~frontend/theme";

import { Chart } from "./Chart";
import { ChartContainer } from "./ChartContainer";
import { maybeNumberToPercentage } from "./numbers";

type Data = {
  campaignStartDate: string;
  replyRate: number | null;
  optOutRate: number | null;
  conversations: number | null;
}[];

export function RepliesAndOptOutsChart({
  data,
}: {
  data: Data | null;
}): ReactElement {
  const dataWithMissingDates: Data | null = useMemo(() => {
    if (!data) {
      return null;
    }

    if (data.length === 0 || data.length === 1) {
      return data;
    }
    const dataByDate = _.keyBy(data, "campaignStartDate");

    const minDate = DateTime.fromISO(data[0].campaignStartDate);
    const maxDate = DateTime.fromISO(data[data.length - 1].campaignStartDate);

    const res: Data = [];
    let date = minDate;
    while (date <= maxDate) {
      const isoDate = date.toISODate()!;

      res.push({
        campaignStartDate: isoDate,
        replyRate: dataByDate[isoDate]?.replyRate ?? 0,
        optOutRate: dataByDate[isoDate]?.optOutRate ?? 0,
        conversations: dataByDate[isoDate]?.conversations ?? 0,
      });

      date = date.plus({ day: 1 });
    }

    return res;
  }, [data]);

  return (
    <ChartContainer
      title="Replies and Opt Outs"
      loading={!data}
      downloadableRows={data?.map(r => ({
        campaignStartDate: r.campaignStartDate,
        replyRate: (r.replyRate ?? 0).toFixed(4),
        optOutRate: (r.optOutRate ?? 0).toFixed(4),
        conversations: (r.conversations ?? 0).toFixed(0),
      }))}
      downloadFileName="replies_and_opt_outs.csv"
      empty={data?.length === 0}
    >
      <Chart
        data={dataWithMissingDates ?? []}
        series={[
          {
            type: "line",
            yAxisId: "rates",
            label: "Reply Rate",
            color: palette.darkGreen,
            getValue: d => d.replyRate,
          },
          {
            type: "line",
            yAxisId: "rates",
            color: palette.red,
            label: "Opt Out Rate",
            getValue: d => d.optOutRate,
          },
          {
            type: "bar",
            yAxisId: "conversations",
            color: palette.blue,
            label: "Conversations",
            getValue: d => d.conversations,
          },
        ]}
        getXAxisValue={d => d.campaignStartDate}
        xAxisLabel="Campaign Start Date"
        xAxisTicks={10}
        yAxes={{
          rates: {
            position: "left",
            label: "Reply and Opt Out Rate",
            formatValue: v => maybeNumberToPercentage(v, 2)!,
            formatTick: v => maybeNumberToPercentage(v, 0)!,
          },
          conversations: {
            position: "right",
            label: "Conversations",
          },
        }}
        height={280}
      />
    </ChartContainer>
  );
}
